<script lang="ts">
const DEFAULT_MIN_DATASOURCE_PERIOD_SECONDS = 86400
</script>
<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { getCurrentInstance, onMounted } from 'vue';
import ModalDialog from '@/components/modal-dialog/Modal-Dialog.vue';
import { validateCron } from '@/utils/cron-validators';
import { usePlans, PlanTypes } from '@/composables/plans';
import i18n from '@/i18n';



interface InputCronProps {
  isDatasource?: boolean;
  hint?: string;
  minDefaultPeriod?: number;
}

const props = defineProps({
  isDatasource: { type: Boolean },
  hint: null,
  minDefaultPeriod: null
});
const emit = defineEmits(['input']);

const vm = getCurrentInstance();
const plans = usePlans();

let isPlanLoaded = _ref(false);
const modalDialog = _ref<InstanceType<typeof ModalDialog> | null>(null);
const inputCron = _ref<any>(null);

const cronItems = [
  { text: '@minutely', duration: 60 },
  { text: '@hourly', duration: 3600 },
  { text: '@daily', duration: 86400 },
  { text: '@weekly', duration: 604800 },
  { text: '@monthly', duration: 2419200 },
];

const headers = [
  { text: 'Entry', value: 'entry' },
  { text: 'Description', value: 'desc' },
  { text: 'Equivalent to', value: 'eqt' },
];

const items = [
  { entry: '@yearly (or @annually)', desc: i18n.t('cron.run_once_a_year'), eqt: '0 0 1 1 *' },
  { entry: ' @monthly', desc: i18n.t('cron.run_once_a_month'), eqt: '0 0 1 * *' },
  { entry: '@weekly', desc: i18n.t('cron.run_once_a_week'), eqt: '0 0 * * 0' },
  { entry: '@daily (or @midnight)', desc: i18n.t('cron.run_once_a_day'), eqt: '0 0 * * *' },
  { entry: '@hourly', desc: i18n.t('cron.run_once_an_hour'), eqt: '0 * * * *' },
  { entry: '@minutely', desc: i18n.t('cron.run_every_minute'), eqt: '0 * * * *' },
];

const minDatasourcePeriod = _computed(() => {
  if (!isPlanLoaded.value) return DEFAULT_MIN_DATASOURCE_PERIOD_SECONDS;
  return plans.getIntegrationPlan.value?.minIntegrationPeriodSeconds
    ?? DEFAULT_MIN_DATASOURCE_PERIOD_SECONDS;
});

const minPeriod = _computed(() => (props.isDatasource ? minDatasourcePeriod.value : (props.minDefaultPeriod ?? 3600)));

const cronRules = _computed(() => [
  (value: string) => validateCron(value, minPeriod.value),
]);

const validCronItems = _computed(() => cronItems.filter((item) => item.duration >= minPeriod.value));

const hintLabel = _computed(() => props.hint || i18n.t('cron.input_hint'));
const attrsValue = _computed(() => vm?.proxy?.$attrs.value);
const isNull = _computed(() => attrsValue.value === null);
const canRemove = _computed(() => 'can-remove' in (vm?.proxy?.$attrs ?? {}));
const label = _computed(() => (isNull.value ? 'Remove existing' : 'Schedule'));
const labelClass = _computed(() => (isNull.value ? 'warning--text' : ''));
const appendIcon = _computed(() => (isNull.value || attrsValue.value?.length ? 'icon-dismiss' : ''));

const openDialog = () => {
  modalDialog.value?.openDialog();
};

const closeMenu = () => {
  if (inputCron.value?.isMenuActive) {
    setTimeout(() => inputCron.value?.blur?.(), 0);
  }
};

const clear = () => {
  emit('input', undefined);
  closeMenu();
};

const toggleNull = () => {
  if (!isNull.value) emit('input', null);
  else emit('input', []);
  closeMenu();
};

onMounted(async () => {
  try {
    await plans.refreshPlan(PlanTypes.INTEGRATION);
  } finally {
    isPlanLoaded.value = true;
  }
});
</script>

<template lang="pug">
.input-cron
  v-text-field(
    ref="inputCron"
    v-bind="$attrs"
    v-on="$listeners"
    :rules="cronRules"
    :items="validCronItems"
    :return-object="false"
    :append-icon="appendIcon"
    @click:append-outer="openDialog"
    @click="closeMenu"
    @click:append="clear"
    :hint="hintLabel"
    append-outer-icon="icon-question-circle-outline"
    data-track="cron"
    item-text="text" dense persistent-hint outlined)

    template(v-slot:label)
      .d-flex
        v-icon( class="mr-1" v-if="isNull" color="iconWarning" small ) icon-warning-fill
        span( :class="labelClass" ) {{ label }}

    template(v-if="canRemove" v-slot:prepend-item)
      v-list-item( ripple @mousedown.prevent @click='toggleNull' )
        v-list-item-content
          v-list-item-title {{ $t('cron.remove_existing') }}
      v-divider.mt-2

  ModalDialog( ref="modalDialog" :title="$t('cron.cron_syntax')" )

    template( v-slot:body )
      .mt-2 {{ $t('cron.a_cron_expression') }}
      pre.mt-2.pre {{ $t('cron.cron-text1') }}
      .mt-2 {{ $t('cron.for_example') }}
        code {{ $t('cron.cron-text2') }}
      .mt-2 {{ $t('cron.there_are_also_predefined_cron') }}
        v-data-table(dense :headers="headers" :items="items" item-key='name' hide-default-footer)
      .mt-2
        | {{ $t('cron.to_generate_cronjob') }}
        a(href='https://crontab.guru/' target='_blank')  {{ $t('cron.crontab_guru') }}

</template>

<style lang="scss" scoped>
::v-deep.v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined {
  .v-input__append-outer {
    margin-top: 7px !important;
  }
}

.pre {
  background-color: var(--v-bgSecondary-base);
  padding: 1rem;
  border-radius: 0.25rem;
}
</style>
